import { createContext, useContext } from "react";
import { WifiOff } from "lucide-react";
import { useT } from "@/i18n";
import type { ConnectionState } from "@/lib/sse";

/**
 * Whether what is on screen can still be trusted.
 *
 * The board is the surface that needs this: dragging a card while the stream is down means
 * acting on figures that may already be wrong, and — worse — a move whose result the user
 * would not see. So freshness is application state, not a detail of the SSE client, and
 * the board reads it here rather than owning its own connection.
 */
const ConnectionContext = createContext<ConnectionState>("connecting");

export const ConnectionProvider = ConnectionContext.Provider;

export function useConnectionState(): ConnectionState {
  return useContext(ConnectionContext);
}

/** True while live updates are not arriving — the board disables dragging on this. */
export function useIsDisconnected(): boolean {
  const state = useConnectionState();
  return state === "reconnecting" || state === "stale";
}

/**
 * The banner. `connecting` is deliberately silent: a first connection taking a moment is
 * not a problem, and announcing it on every page load would train people to ignore this.
 */
export function ConnectionBanner({ state }: { state: ConnectionState }) {
  const t = useT();

  if (state === "live" || state === "connecting") return null;

  return (
    <div
      role="status"
      className="border-warning/40 bg-warning/10 flex items-center justify-center gap-2 border-b px-4 py-1.5 text-sm"
    >
      <WifiOff className="h-4 w-4 shrink-0" aria-hidden />
      {state === "reconnecting" ? t("connection.reconnecting") : t("connection.stale")}
    </div>
  );
}
